import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { recordPageVisit, getStaffDashboard } from "@/lib/api";
import { 
  Users, 
  MessageSquare, 
  BookMarked,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Info,
  Activity,
  BarChart3,
  PieChart,
  Clock,
  Loader2,
  Brain,
  Heart,
  Mail,
  RefreshCw,
  Calendar,
  Filter
} from "lucide-react";

const insightIcons = {
  positive: CheckCircle,
  warning: AlertCircle,
  alert: AlertCircle,
  info: Info
};

const insightColors = {
  positive: "text-green-600 bg-green-50 border-green-200",
  warning: "text-amber-600 bg-amber-50 border-amber-200",
  alert: "text-red-600 bg-red-50 border-red-200",
  info: "text-blue-600 bg-blue-50 border-blue-200"
};

const TIMEFRAME_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "Last 7 Days" },
  { value: "month", label: "Last 30 Days" },
  { value: "quarter", label: "Last 90 Days" },
  { value: "year", label: "Last 12 Months" },
  { value: "custom", label: "Custom Range" }
];

export default function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState("week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  useEffect(() => {
    recordPageVisit("staff-dashboard").catch(console.error);
    if (user && (user.role === "admin" || user.role === "administrator")) {
      loadDashboard();
    }
  }, [user]);

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "administrator") && timeframe !== "custom") {
      loadDashboard();
    }
  }, [timeframe]);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getStaffDashboard(
        timeframe,
        timeframe === "custom" ? customStartDate : null,
        timeframe === "custom" ? customEndDate : null
      );
      setDashboardData(data);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
      setError(err.response?.data?.detail || "Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      loadDashboard();
    }
  };

  if (!user || (user.role !== "admin" && user.role !== "administrator")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
            <p className="text-muted-foreground mb-4">
              This portal is only available to Goodwin Cares administrators.
            </p>
            <Button onClick={() => navigate("/")} className="rounded-full">
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Error Loading Dashboard</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadDashboard} className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { engagement, journal_activity, chat_activity, feature_usage, symptom_trends, mood_trends, action_items, ai_insights, disease_distribution, memory_activity, timeframe: timeframeData } = dashboardData || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/50 to-background" data-testid="staff-dashboard">
      {/* Header */}
      <section className="section-padding bg-gradient-to-br from-teal-50 to-background pb-8">
        <div className="container-max">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <Badge variant="secondary" className="mb-2">
                <Activity className="w-3 h-3 mr-1" />
                Staff Dashboard
              </Badge>
              <h1 className="heading-2 text-foreground">
                Family Engagement <span className="gradient-text">Insights</span>
              </h1>
              <p className="text-muted-foreground mt-1">
                AI-powered analytics to help you support families better
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={loadDashboard} variant="outline" className="rounded-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Timeframe Selector */}
          <div className="mt-6 p-4 bg-white rounded-xl shadow-soft">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <span className="font-medium">Timeframe:</span>
              </div>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEFRAME_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {timeframe === "custom" && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-40"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-40"
                  />
                  <Button onClick={handleCustomDateApply} size="sm">Apply</Button>
                </div>
              )}
              
              {timeframeData && (
                <Badge variant="outline" className="ml-auto">
                  <Clock className="w-3 h-3 mr-1" />
                  {timeframeData.label}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* AI Insights Section */}
      {ai_insights && ai_insights.length > 0 && (
        <section className="py-4 px-6 md:px-12">
          <div className="container-max">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-primary" />
              <h2 className="font-semibold">AI Insights</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ai_insights.map((insight, index) => {
                const Icon = insightIcons[insight.type] || Info;
                const colorClass = insightColors[insight.type] || insightColors.info;
                return (
                  <Card key={index} className={`border ${colorClass.split(' ')[2]}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${colorClass.split(' ')[1]}`}>
                          <Icon className={`w-5 h-5 ${colorClass.split(' ')[0]}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-sm">{insight.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{insight.message}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Action Items */}
      <section className="py-4 px-6 md:px-12">
        <div className="container-max">
          <div className="grid grid-cols-3 gap-4">
            <Card className={action_items?.unread_messages > 0 ? "border-red-200 bg-red-50" : ""}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Unread Messages</p>
                    <p className="text-3xl font-bold">{action_items?.unread_messages || 0}</p>
                  </div>
                  <MessageSquare className={`w-8 h-8 ${action_items?.unread_messages > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                </div>
                {action_items?.unread_messages > 0 && (
                  <Button 
                    size="sm" 
                    className="w-full mt-3 rounded-full"
                    onClick={() => navigate("/messages")}
                  >
                    View Messages
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Open Conversations</p>
                    <p className="text-3xl font-bold">{action_items?.open_conversations || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card className={action_items?.new_contacts > 0 ? "border-amber-200 bg-amber-50" : ""}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">New Contact Requests</p>
                    <p className="text-3xl font-bold">{action_items?.new_contacts || 0}</p>
                  </div>
                  <Mail className={`w-8 h-8 ${action_items?.new_contacts > 0 ? 'text-amber-500' : 'text-muted-foreground'}`} />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Engagement Metrics */}
      <section className="py-4 px-6 md:px-12">
        <div className="container-max">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Engagement Overview
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Total Families</p>
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <p className="text-3xl font-bold">{engagement?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  +{engagement?.new_users_week || 0} this week
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Active This Week</p>
                  <Activity className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-3xl font-bold">{engagement?.active_users_week || 0}</p>
                <div className="mt-2">
                  <Progress value={engagement?.engagement_rate || 0} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-1">
                    {engagement?.engagement_rate || 0}% engagement rate
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Journal Users</p>
                  <BookMarked className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-3xl font-bold">{journal_activity?.users_journaling || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {journal_activity?.entries_this_week || 0} entries this week
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">AI Chat Users</p>
                  <MessageSquare className="w-5 h-5 text-teal-500" />
                </div>
                <p className="text-3xl font-bold">{chat_activity?.users_chatting || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {chat_activity?.sessions_this_week || 0} sessions this week
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trends Section */}
      <section className="py-4 px-6 md:px-12">
        <div className="container-max grid md:grid-cols-2 gap-6">
          {/* Symptom Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-primary" />
                Top Reported Symptoms
              </CardTitle>
              <CardDescription>This week's most common symptoms in journals</CardDescription>
            </CardHeader>
            <CardContent>
              {symptom_trends && symptom_trends.length > 0 ? (
                <div className="space-y-3">
                  {symptom_trends.map((symptom, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm">{symptom.symptom}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full" 
                            style={{ width: `${(symptom.count / symptom_trends[0].count) * 100}%` }}
                          />
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {symptom.count}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No symptom data available
                </p>
              )}
            </CardContent>
          </Card>

          {/* Mood Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Heart className="w-5 h-5 text-pink-500" />
                Mood Trends
              </CardTitle>
              <CardDescription>Average patient mood over the past week</CardDescription>
            </CardHeader>
            <CardContent>
              {mood_trends && mood_trends.length > 0 ? (
                <div className="space-y-3">
                  {mood_trends.map((day, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm w-20">{day.date.slice(5)}</span>
                      <div className="flex items-center gap-2 flex-1 justify-end">
                        <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              day.avg_mood >= 4 ? 'bg-green-500' : 
                              day.avg_mood >= 3 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${(day.avg_mood / 5) * 100}%` }}
                          />
                        </div>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${
                            day.avg_mood >= 4 ? 'bg-green-100 text-green-700' : 
                            day.avg_mood >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {day.avg_mood}/5
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No mood data available
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Usage & Distribution */}
      <section className="py-4 px-6 md:px-12 pb-12">
        <div className="container-max grid md:grid-cols-2 gap-6">
          {/* Feature Usage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <PieChart className="w-5 h-5 text-primary" />
                Feature Usage
              </CardTitle>
              <CardDescription>Most visited pages this week</CardDescription>
            </CardHeader>
            <CardContent>
              {feature_usage && feature_usage.length > 0 ? (
                <div className="space-y-2">
                  {feature_usage.slice(0, 6).map((feature, index) => (
                    <div key={index} className="flex items-center justify-between py-1">
                      <span className="text-sm capitalize">{feature.feature || 'Home'}</span>
                      <Badge variant="outline">{feature.count} visits</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No usage data available
                </p>
              )}
            </CardContent>
          </Card>

          {/* Disease Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5 text-primary" />
                Patient Conditions
              </CardTitle>
              <CardDescription>Distribution of disease categories</CardDescription>
            </CardHeader>
            <CardContent>
              {disease_distribution && disease_distribution.length > 0 ? (
                <div className="space-y-2">
                  {disease_distribution.map((disease, index) => (
                    <div key={index} className="flex items-center justify-between py-1">
                      <span className="text-sm capitalize">{disease.disease?.replace('_', ' ') || 'Not specified'}</span>
                      <Badge variant="secondary">{disease.count} patients</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No disease data available
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
